import {
    Box,
    Flex,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    Button,
    Text,
    Image,
    IconButton,
    Avatar,
    Tooltip,
} from "@chakra-ui/react";
import { ChevronDownIcon, ChevronRightIcon, HamburgerIcon, LockIcon, SettingsIcon } from "@chakra-ui/icons";
import { useState } from "react";
import ReportPopupProduksi from "./report/production/ReportPopupProduksi";
import { RainfallReportPopup } from "./report/rainfall";
import WaterLevelReportPopup from "./report/water_level/WaterLevelReportPopup";
import FactoryReceiptReportPopup from "./report/factoryReceipt/FactoryReceiptReportPopup";
import Reports from "./report/inspection/Reports";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { useCanAccess } from "../rbac/useCanAccess";

const Navbar = () => {
    const [openMenu, setOpenMenu] = useState<string | null>(null);
    const [openSubmenus, setOpenSubmenus] = useState<Set<string>>(new Set());
    const [isReportOpen, setIsReportOpen] = useState(false);
    const [isReportProduksiOpen, setIsReportProduksiOpen] = useState(false);
    const [isRainfallReportOpen, setIsRainfallReportOpen] = useState(false);
    const [isWaterLevelReportOpen, setIsWaterLevelReportOpen] = useState(false);
    const [isFactoryReceiptReportOpen, setIsFactoryReceiptReportOpen] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const { canAccess } = useCanAccess();

    // Untuk semua device - toggle menu saat diklik
    const handleMenuClick = (menuName: string) => {
        setOpenMenu(prev => prev === menuName ? null : menuName);
        // Reset submenu when changing main menu
        if (openMenu !== menuName) {
            setOpenSubmenus(new Set());
        }
    };

    // Handle submenu toggle
    const handleSubmenuClick = (e: React.MouseEvent, submenuName: string) => {
        e.stopPropagation();
        setOpenSubmenus(prev => {
            const newSet = new Set(prev);
            if (newSet.has(submenuName)) {
                newSet.delete(submenuName);
            } else {
                newSet.add(submenuName);
            }
            return newSet;
        });
    };

    return (
        <Flex
            w="100%"
            h={{ base: "60px", md: "40px" }}
            bg="#4A5A33"
            position="relative"
            px={{ base: 3, md: 0 }}
        >

            {/* Mobile Menu Button */}
            <Flex
                align="center"
                display={{ base: "flex", md: "none" }}
                w="60px"
                h="60px"
                ml={-3}
                pl={3}
                zIndex={20}
            >
                <IconButton
                    aria-label="Open menu"
                    icon={<HamburgerIcon boxSize={6} />} // Ikon lebih besar
                    variant="ghost"
                    color="white"
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    size="lg"
                    _active={{ bg: "transparent" }}
                    _hover={{ bg: "rgba(255,255,255,0.1)" }}
                />
            </Flex>

            {/* Desktop User Profile - Positioned on the right */}
            <Box
                display={{ base: "none", md: "block" }}
                position="absolute"
                left={{ md: "0px" }}
                top="50%"
                transform="translateY(-50%)"
                zIndex={10}
            >
                <Menu
                    isOpen={openMenu === "user"}
                    onClose={() => setOpenMenu(null)}
                    strategy="fixed"
                    computePositionOnMount
                    gutter={0}
                    isLazy
                    autoSelect={false}
                >
                    <MenuButton
                        as={Button}
                        variant="ghost"
                        color="white"
                        fontWeight="bold"
                        _active={{ bg: "transparent" }}
                        _hover={{ bg: "rgba(255,255,255,0.1)" }}
                        _expanded={{ bg: "rgba(255,255,255,0.1)" }}
                        onClick={() => handleMenuClick("user")}
                        p={2}
                    >
                        <Tooltip label={user?.email || "User"} placement="bottom">
                            <Avatar size="sm" name={user?.email || "User"} bg="green.500" />
                        </Tooltip>
                    </MenuButton>
                    <MenuList
                        bg="white"
                        color="black"
                        mt={2}
                        minW="200px"
                        boxShadow="lg"
                        border="1px solid"
                        borderColor="gray.200"
                        py={0}
                    >
                        <Box px={4} py={3} borderBottom="1px solid" borderColor="gray.100">
                            <Flex align="center">
                                <Avatar size="sm" name={user?.email || "User"} bg="green.500" mr={3} />
                                <Box>
                                    <Text fontSize="sm" fontWeight="medium" color="gray.700">
                                        {user?.email || "User"}
                                    </Text>
                                    <Text fontSize="xs" color="gray.500">
                                        Online
                                    </Text>
                                </Box>
                            </Flex>
                        </Box>
                        {canAccess('/admin') && (
                            <MenuItem
                                onClick={() => {
                                    navigate('/admin');
                                    setOpenMenu(null);
                                    setIsMobileMenuOpen(false);
                                }}
                                icon={<SettingsIcon />}
                                _hover={{ bg: "gray.100" }}
                                _focus={{ bg: "gray.100" }}
                                py={3}
                            >
                                <Box flex="1">Admin Dashboard</Box>
                            </MenuItem>
                        )}
                        <MenuItem
                            onClick={() => {
                                logout();
                                setOpenMenu(null);
                                setIsMobileMenuOpen(false);
                            }}
                            icon={<LockIcon />}
                            _hover={{ bg: "gray.100" }}
                            _focus={{ bg: "gray.100" }}
                            py={3}
                            color="red.600"
                        >
                            <Box flex="1">Logout</Box>
                        </MenuItem>
                    </MenuList>
                </Menu>
            </Box>

            {/* Main Menu Items */}
            <Flex
                flex="1"
                color="white"
                align="center"
                justify={{ base: "flex-end", md: "center" }}
                gap={{ base: 2, md: 8 }}
                bg="#4A5A33"
            >
                {/* Mobile Menu Overlay */}
                {isMobileMenuOpen && (
                    <Box
                        position="fixed"
                        top="60px"
                        left={0}
                        right={0}
                        bottom={0}
                        bg="rgba(0,0,0,0.9)"
                        zIndex={20}
                        onClick={() => setIsMobileMenuOpen(false)}
                    />
                )}

                {/* Menu Items Container */}
                <Flex
                    display={{ base: isMobileMenuOpen ? "flex" : "none", md: "flex" }}
                    direction={{ base: "column", md: "row" }}
                    position={{ base: "absolute", md: "relative" }}
                    top={{ base: "60px", md: "auto" }}
                    left={0}
                    width={{ base: "100%", md: "auto" }}
                    bg={{ base: "#4A5A33", md: "transparent" }}
                    zIndex={30}
                    py={{ base: 4, md: 0 }}
                    pl={{ base: 4, md: 0 }}
                >
                    {/* Mobile User Menu - Only shown in mobile hamburger menu */}
                    <Box
                        display={{ base: "block", md: "none" }}
                        position="relative"
                        width="100%"
                        mb={3}
                        order={0}
                    >
                        <Menu
                            isOpen={openMenu === "user"}
                            onClose={() => setOpenMenu(null)}
                            strategy="fixed"
                            computePositionOnMount
                            gutter={0}
                            isLazy
                            autoSelect={false}
                        >
                            <MenuButton
                                as={Button}
                                variant="ghost"
                                color="white"
                                fontWeight="bold"
                                _active={{ bg: "transparent" }}
                                width="100%"
                                justifyContent="space-between"
                                textAlign="left"
                                px={3}
                                py={3}
                                _hover={{ bg: "rgba(255,255,255,0.1)" }}
                                _expanded={{ bg: "rgba(255,255,255,0.1)" }}
                                onClick={() => handleMenuClick("user")}
                            >
                                <Flex align="center">
                                    <Avatar size="xs" name={user?.email || "User"} bg="green.500" />
                                    <Text ml={2}>
                                        {user?.email || "User"}
                                    </Text>
                                    <ChevronDownIcon ml="auto" />
                                </Flex>
                            </MenuButton>
                            <MenuList
                                bg="white"
                                color="black"
                                mt={0}
                                borderTopRadius={0}
                                minW="calc(100vw - 32px)"
                                position="relative"
                                border="none"
                                py={0}
                            >
                                <Box px={4} py={3} borderBottom="1px solid" borderColor="gray.100">
                                    <Flex align="center">
                                        <Avatar size="sm" name={user?.email || "User"} bg="green.500" mr={3} />
                                        <Box>
                                            <Text fontSize="sm" fontWeight="medium" color="gray.700">
                                                {user?.email || "User"}
                                            </Text>
                                            <Text fontSize="xs" color="gray.500">
                                                Online
                                            </Text>
                                        </Box>
                                    </Flex>
                                </Box>
                                {canAccess('/admin') && (
                                    <MenuItem
                                        onClick={() => {
                                            navigate('/admin');
                                            setOpenMenu(null);
                                            setIsMobileMenuOpen(false);
                                        }}
                                        icon={<SettingsIcon />}
                                        _hover={{ bg: "gray.100" }}
                                        _focus={{ bg: "gray.100" }}
                                        py={3}
                                    >
                                        <Box flex="1">Admin Dashboard</Box>
                                    </MenuItem>
                                )}
                                <MenuItem
                                    onClick={() => {
                                        logout();
                                        setOpenMenu(null);
                                        setIsMobileMenuOpen(false);
                                    }}
                                    icon={<LockIcon />}
                                    _hover={{ bg: "gray.100" }}
                                    _focus={{ bg: "gray.100" }}
                                    py={3}
                                    color="red.600"
                                >
                                    <Box flex="1">Logout</Box>
                                </MenuItem>
                            </MenuList>
                        </Menu>
                    </Box>

                    {/* Menu Home */}
                    {canAccess('/') && (
                        <Box
                            width={{ base: "100%", md: "auto" }}
                            mb={{ base: 3, md: 0 }}
                            mr={{ base: 0, md: 3 }}
                            order={1}
                        >
                            <Button
                                variant="ghost"
                                color="white"
                                fontWeight="bold"
                                width={{ base: "100%", md: "auto" }}
                                justifyContent={{ base: "space-between", md: "center" }}
                                textAlign="left"
                                px={{ base: 3, md: 4 }}
                                py={{ base: 3, md: 2 }}
                                _hover={{ bg: { md: "rgba(255,255,255,0.1)" } }}
                                _active={{ bg: "transparent" }}
                                onClick={() => {
                                    navigate('/');
                                    setOpenMenu(null);
                                    setIsMobileMenuOpen(false);
                                }}
                            >
                                Home
                            </Button>
                        </Box>
                    )}
                    {/* Menu Inspeksi */}
                    {(canAccess('/feeds') || canAccess('/peta-inspeksi') || canAccess('/harvest-comparison')) && (
                        <Box
                            position="relative"
                            width={{ base: "100%", md: "auto" }}
                            mb={{ base: 3, md: 0 }}
                            order={2}
                        >
                            <Menu
                                isOpen={openMenu === "inspeksi"}
                                onClose={() => {
                                    setOpenMenu(null);
                                    setOpenSubmenus(new Set());
                                }}
                                strategy="fixed"
                                computePositionOnMount
                                gutter={0}
                                isLazy
                                autoSelect={false}
                            >
                                <MenuButton
                                    as={Button}
                                    rightIcon={<ChevronDownIcon />}
                                    variant="ghost"
                                    _hover={{ bg: { md: "rgba(255,255,255,0.1)" } }}
                                    _expanded={{ bg: { md: "rgba(255,255,255,0.1)" } }}
                                    color="white"
                                    fontWeight="bold"
                                    _active={{ bg: "transparent" }}
                                    width={{ base: "100%", md: "auto" }}
                                    justifyContent={{ base: "space-between", md: "center" }}
                                    textAlign="left"
                                    px={{ base: 3, md: 4 }}
                                    py={{ base: 3, md: 2 }}
                                    onClick={() => handleMenuClick("inspeksi")}
                                >
                                    Inspeksi
                                </MenuButton>
                                <MenuList
                                    bg="white"
                                    color="black"
                                    mt={0}
                                    borderTopRadius={0}
                                    minW={{ base: "calc(100vw - 32px)", md: "200px" }}
                                    position={{ base: "relative", md: "absolute" }}
                                    boxShadow={{ md: "lg" }}
                                    border={{ base: "none", md: "1px solid" }}
                                    borderColor={{ base: "transparent", md: "gray.200" }}
                                    py={0}
                                >
                                    {canAccess('/feeds') && (
                                        <MenuItem
                                            _hover={{ bg: "gray.100" }}
                                            _focus={{ bg: "gray.100" }}
                                            py={3}
                                            onClick={() => {
                                                navigate('/feeds');
                                                setOpenMenu(null);
                                                setIsMobileMenuOpen(false);
                                            }}
                                        >
                                            <Box flex="1">Linimasa Inspeksi</Box>
                                        </MenuItem>
                                    )}
                                    {canAccess('/harvest-comparison') && (
                                        <MenuItem
                                            _hover={{ bg: "gray.100" }}
                                            _focus={{ bg: "gray.100" }}
                                            py={3}
                                            onClick={() => {
                                                navigate('/harvest-comparison');
                                                setOpenMenu(null);
                                                setIsMobileMenuOpen(false);
                                            }}
                                        >
                                            <Box flex="1">Perbandingan Inspeksi</Box>
                                        </MenuItem>
                                    )}
                                    {canAccess('/peta-inspeksi') && (
                                        <MenuItem
                                            _hover={{ bg: "gray.100" }}
                                            _focus={{ bg: "gray.100" }}
                                            py={3}
                                            onClick={() => {
                                                navigate('/peta-inspeksi');
                                                setOpenMenu(null);
                                                setIsMobileMenuOpen(false);
                                            }}
                                        >
                                            <Box flex="1">Peta Inspeksi</Box>
                                        </MenuItem>
                                    )}

                                    {/* Form Submenu - moved from Operational */}
                                    {(canAccess('/operational/mutu-ancak') || canAccess('/operational/mutu-buah')) && (
                                        <>
                                            <Box
                                                display="flex"
                                                justifyContent="space-between"
                                                alignItems="center"
                                                cursor="pointer"
                                                px={3}
                                                py={3}
                                                _hover={{ bg: "gray.100" }}
                                                onClick={(e) => handleSubmenuClick(e, "form-inspeksi")}
                                            >
                                                <Box flex="1">Form</Box>
                                                <ChevronRightIcon
                                                    transform={openSubmenus.has("form-inspeksi") ? "rotate(90deg)" : "rotate(0deg)"}
                                                    transition="transform 0.2s"
                                                    fontSize="sm"
                                                />
                                            </Box>

                                            {openSubmenus.has("form-inspeksi") && (
                                                <>
                                                    {canAccess('/operational/mutu-ancak') && (
                                                        <MenuItem
                                                            _hover={{ bg: "gray.100" }}
                                                            _focus={{ bg: "gray.100" }}
                                                            py={3}
                                                            pl={8}
                                                            onClick={() => {
                                                                navigate('/operational/mutu-ancak');
                                                                setOpenMenu(null);
                                                                setIsMobileMenuOpen(false);
                                                            }}
                                                        >
                                                            <Box flex="1">Mutu Ancak</Box>
                                                        </MenuItem>
                                                    )}
                                                    {canAccess('/operational/mutu-buah') && (
                                                        <MenuItem
                                                            _hover={{ bg: "gray.100" }}
                                                            _focus={{ bg: "gray.100" }}
                                                            py={3}
                                                            pl={8}
                                                            onClick={() => {
                                                                navigate('/operational/mutu-buah');
                                                                setOpenMenu(null);
                                                                setIsMobileMenuOpen(false);
                                                            }}
                                                        >
                                                            <Box flex="1">Mutu Buah</Box>
                                                        </MenuItem>
                                                    )}
                                                </>
                                            )}
                                        </>
                                    )}

                                    {canAccess('/peta-inspeksi') && (
                                        <MenuItem
                                            _hover={{ bg: "gray.100" }}
                                            _focus={{ bg: "gray.100" }}
                                            py={3}
                                            onClick={() => {
                                                setIsReportOpen(true);
                                                setOpenMenu(null);
                                                setIsMobileMenuOpen(false);
                                            }}
                                        >
                                            <Box flex="1">Unduh Laporan</Box>
                                        </MenuItem>
                                    )}
                                </MenuList>
                            </Menu>
                        </Box>
                    )}

                    {/* Menu Produksi */}
                    {(canAccess('/mill-dashboard') || canAccess('/estate-dashboard') || canAccess('/estate-map')) && (
                        <Box
                            position="relative"
                            width={{ base: "100%", md: "auto" }}
                            mb={{ base: 3, md: 0 }}
                            order={3}
                        >
                            <Menu
                                isOpen={openMenu === "produksi"}
                                onClose={() => setOpenMenu(null)}
                                strategy="fixed"
                                computePositionOnMount
                                gutter={0}
                                isLazy
                                autoSelect={false}
                            >
                                <MenuButton
                                    as={Button}
                                    rightIcon={<ChevronDownIcon />}
                                    variant="ghost"
                                    _hover={{ bg: { md: "rgba(255,255,255,0.1)" } }}
                                    _expanded={{ bg: { md: "rgba(255,255,255,0.1)" } }}
                                    color="white"
                                    fontWeight="bold"
                                    _active={{ bg: "transparent" }}
                                    width={{ base: "100%", md: "auto" }}
                                    justifyContent={{ base: "space-between", md: "center" }}
                                    textAlign="left"
                                    px={{ base: 3, md: 4 }}
                                    py={{ base: 3, md: 2 }}
                                    onClick={() => handleMenuClick("produksi")}
                                >
                                    Produksi
                                </MenuButton>
                                <MenuList
                                    bg="white"
                                    color="black"
                                    mt={0}
                                    borderTopRadius={0}
                                    minW={{ base: "calc(100vw - 32px)", md: "220px" }}
                                    position={{ base: "relative", md: "absolute" }}
                                    boxShadow={{ md: "lg" }}
                                    border={{ base: "none", md: "1px solid" }}
                                    borderColor={{ base: "transparent", md: "gray.200" }}
                                    py={0}
                                >
                                    {canAccess('/mill-dashboard') && (
                                        <MenuItem
                                            _hover={{ bg: "gray.100" }}
                                            _focus={{ bg: "gray.100" }}
                                            py={3}
                                            onClick={() => {
                                                navigate('/mill-dashboard');
                                                setOpenMenu(null);
                                                setIsMobileMenuOpen(false);
                                            }}
                                        >
                                            <Box flex="1">Dashboard Mill</Box>
                                        </MenuItem>
                                    )}
                                    {canAccess('/estate-dashboard') && (
                                        <MenuItem
                                            _hover={{ bg: "gray.100" }}
                                            _focus={{ bg: "gray.100" }}
                                            py={3}
                                            onClick={() => {
                                                navigate('/estate-dashboard');
                                                setOpenMenu(null);
                                                setIsMobileMenuOpen(false);
                                            }}
                                        >
                                            <Box flex="1">Dashboard Kebun</Box>
                                        </MenuItem>
                                    )}
                                    {canAccess('/estate-map') && (
                                        <MenuItem
                                            _hover={{ bg: "gray.100" }}
                                            _focus={{ bg: "gray.100" }}
                                            py={3}
                                            onClick={() => {
                                                setIsReportProduksiOpen(true);
                                                setOpenMenu(null);
                                                setIsMobileMenuOpen(false);
                                            }}
                                        >
                                            <Box flex="1">Unduh Laporan</Box>
                                        </MenuItem>
                                    )}
                                </MenuList>
                            </Menu>
                        </Box>
                    )}
                    {/* Menu Sustainability */}
                    {(canAccess('/hotspot') || canAccess('/wms')) && (
                        <Box
                            position="relative"
                            width={{ base: "100%", md: "auto" }}
                            mb={{ base: 3, md: 0 }}
                            order={3}
                        >
                            <Menu
                                isOpen={openMenu === "sustainability"}
                                onClose={() => setOpenMenu(null)}
                                strategy="fixed"
                                computePositionOnMount
                                gutter={0}
                                isLazy
                                autoSelect={false}
                            >
                                <MenuButton
                                    as={Button}
                                    rightIcon={<ChevronDownIcon />}
                                    variant="ghost"
                                    _hover={{ bg: { md: "rgba(255,255,255,0.1)" } }}
                                    _expanded={{ bg: { md: "rgba(255,255,255,0.1)" } }}
                                    color="white"
                                    fontWeight="bold"
                                    _active={{ bg: "transparent" }}
                                    width={{ base: "100%", md: "auto" }}
                                    justifyContent={{ base: "space-between", md: "center" }}
                                    textAlign="left"
                                    px={{ base: 3, md: 4 }}
                                    py={{ base: 3, md: 2 }}
                                    onClick={() => handleMenuClick("sustainability")}
                                >
                                    Sustainability
                                </MenuButton>
                                <MenuList
                                    bg="white"
                                    color="black"
                                    mt={0}
                                    borderTopRadius={0}
                                    minW={{ base: "calc(100vw - 32px)", md: "220px" }}
                                    position={{ base: "relative", md: "absolute" }}
                                    boxShadow={{ md: "lg" }}
                                    border={{ base: "none", md: "1px solid" }}
                                    borderColor={{ base: "transparent", md: "gray.200" }}
                                    py={0}
                                >
                                    {canAccess('/hotspot') && (
                                        <MenuItem
                                            _hover={{ bg: "gray.100" }}
                                            _focus={{ bg: "gray.100" }}
                                            py={3}
                                            onClick={() => {
                                                navigate('/hotspot');
                                                setOpenMenu(null);
                                                setIsMobileMenuOpen(false);
                                            }}
                                        >
                                            <Box flex="1">Hotspots</Box>
                                        </MenuItem>
                                    )}
                                    {canAccess('/wms') && (
                                        <MenuItem
                                            _hover={{ bg: "gray.100" }}
                                            _focus={{ bg: "gray.100" }}
                                            py={3}
                                            onClick={() => {
                                                navigate('/wms');
                                                setOpenMenu(null);
                                                setIsMobileMenuOpen(false);
                                            }}
                                        >
                                            <Box flex="1">Water Level</Box>
                                        </MenuItem>
                                    )}
                                    {canAccess('/wms') && (
                                        <>
                                            <Box
                                                display="flex"
                                                justifyContent="space-between"
                                                alignItems="center"
                                                cursor="pointer"
                                                px={3}
                                                py={3}
                                                _hover={{ bg: "gray.100" }}
                                                onClick={(e) => handleSubmenuClick(e, "laporan-sustainability")}
                                            >
                                                <Box flex="1">Laporan</Box>
                                                <ChevronRightIcon
                                                    transform={openSubmenus.has("laporan-sustainability") ? "rotate(90deg)" : "rotate(0deg)"}
                                                    transition="transform 0.2s"
                                                    fontSize="sm"
                                                />
                                            </Box>

                                            {openSubmenus.has("laporan-sustainability") && (
                                                <>
                                                    <MenuItem
                                                        _hover={{ bg: "gray.100" }}
                                                        _focus={{ bg: "gray.100" }}
                                                        py={3}
                                                        pl={8}
                                                        onClick={() => {
                                                            setIsRainfallReportOpen(true);
                                                            setOpenMenu(null);
                                                            setIsMobileMenuOpen(false);
                                                        }}
                                                    >
                                                        <Box flex="1">Curah Hujan</Box>
                                                    </MenuItem>
                                                    <MenuItem
                                                        _hover={{ bg: "gray.100" }}
                                                        _focus={{ bg: "gray.100" }}
                                                        py={3}
                                                        pl={8}
                                                        onClick={() => {
                                                            setIsWaterLevelReportOpen(true);
                                                            setOpenMenu(null);
                                                            setIsMobileMenuOpen(false);
                                                        }}
                                                    >
                                                        <Box flex="1">Water Level</Box>
                                                    </MenuItem>
                                                </>
                                            )}
                                        </>
                                    )}
                                </MenuList>
                            </Menu>
                        </Box>
                    )}

                    {/* Menu Operational */}
                    {(canAccess('/operational/mutu-ancak') || canAccess('/operational/mutu-buah') || canAccess('/operational/rkh') || canAccess('/operational/taksasi') || canAccess('/operational/upkeep-monitoring') || canAccess('/operational/production-monitoring') || canAccess('/operational/harvest-monitoring') || canAccess('/operational/odoo-vs-sakti') || canAccess('/operational/upkeep-map') || canAccess('/operational/fuel_consumption_vs_lacak')) && (
                        <Box
                            position="relative"
                            width={{ base: "100%", md: "auto" }}
                            mb={{ base: 3, md: 0 }}
                            order={4}
                        >
                            <Menu
                                isOpen={openMenu === "operational"}
                                onClose={() => {
                                    setOpenMenu(null);
                                    setOpenSubmenus(new Set());
                                }}
                                strategy="fixed"
                                computePositionOnMount
                                gutter={0}
                                isLazy
                                autoSelect={false}
                            >
                                <MenuButton
                                    as={Button}
                                    rightIcon={<ChevronDownIcon />}
                                    variant="ghost"
                                    _hover={{ bg: { md: "rgba(255,255,255,0.1)" } }}
                                    _expanded={{ bg: { md: "rgba(255,255,255,0.1)" } }}
                                    color="white"
                                    fontWeight="bold"
                                    _active={{ bg: "transparent" }}
                                    width={{ base: "100%", md: "auto" }}
                                    justifyContent={{ base: "space-between", md: "center" }}
                                    textAlign="left"
                                    px={{ base: 3, md: 4 }}
                                    py={{ base: 3, md: 2 }}
                                    onClick={() => handleMenuClick("operational")}
                                >
                                    Operational
                                </MenuButton>
                                <MenuList
                                    bg="white"
                                    color="black"
                                    mt={0}
                                    borderTopRadius={0}
                                    minW={{ base: "calc(100vw - 32px)", md: "220px" }}
                                    position={{ base: "relative", md: "absolute" }}
                                    boxShadow={{ md: "lg" }}
                                    border={{ base: "none", md: "1px solid" }}
                                    borderColor={{ base: "transparent", md: "gray.200" }}
                                    py={0}
                                >
                                    {/* Monitoring Budget Submenu */}
                                    {(canAccess('/operational/production-monitoring') || canAccess('/operational/upkeep-monitoring')) && (
                                        <>
                                            <Box
                                                display="flex"
                                                justifyContent="space-between"
                                                alignItems="center"
                                                cursor="pointer"
                                                px={3}
                                                py={3}
                                                _hover={{ bg: "gray.100" }}
                                                onClick={(e) => handleSubmenuClick(e, "monitoring-budget")}
                                            >
                                                <Box flex="1">Monitoring Budget</Box>
                                                <ChevronRightIcon
                                                    fontSize="sm"
                                                    transition="transform 0.2s"
                                                    transform={openSubmenus.has("monitoring-budget") ? "rotate(90deg)" : "rotate(0deg)"}
                                                />
                                            </Box>
                                            {openSubmenus.has("monitoring-budget") && (
                                                <>
                                                    {canAccess('/operational/upkeep-monitoring') && (
                                                        <MenuItem
                                                            pl={8}
                                                            _hover={{ bg: "gray.100" }}
                                                            _focus={{ bg: "gray.100" }}
                                                            py={3}
                                                            onClick={() => {
                                                                navigate('/operational/upkeep-monitoring');
                                                                setOpenMenu(null);
                                                                setIsMobileMenuOpen(false);
                                                            }}
                                                        >
                                                            <Box flex="1">Perawatan</Box>
                                                        </MenuItem>
                                                    )}
                                                    {canAccess('/operational/production-monitoring') && (
                                                        <MenuItem
                                                            pl={8}
                                                            _hover={{ bg: "gray.100" }}
                                                            _focus={{ bg: "gray.100" }}
                                                            py={3}
                                                            onClick={() => {
                                                                navigate('/operational/production-monitoring');
                                                                setOpenMenu(null);
                                                                setIsMobileMenuOpen(false);
                                                            }}
                                                        >
                                                            <Box flex="1">Produksi TBS</Box>
                                                        </MenuItem>
                                                    )}
                                                </>
                                            )}
                                        </>
                                    )}

                                    {/* Panen Menu Item - Top Level with Submenu */}
                                    {(canAccess('/operational/harvest-monitoring') || canAccess('/operational/harvest-monitoring-foreman') || canAccess('/operational/harvest-map') || canAccess('/operational/taksasi') || canAccess('/operational/rkh') || canAccess('/operational/odoo-vs-sakti') || canAccess('/operational/bkm-muat') || canAccess('/operational/weighbridge')) && (
                                        <>
                                            <Box
                                                display="flex"
                                                justifyContent="space-between"
                                                alignItems="center"
                                                cursor="pointer"
                                                px={3}
                                                py={3}
                                                _hover={{ bg: "gray.100" }}
                                                onClick={(e) => handleSubmenuClick(e, "panen")}
                                            >
                                                <Box flex="1">Panen Sakti</Box>
                                                <ChevronRightIcon
                                                    transform={openSubmenus.has("panen") ? "rotate(90deg)" : "rotate(0deg)"}
                                                    transition="transform 0.2s"
                                                    fontSize="sm"
                                                />
                                            </Box>

                                            {openSubmenus.has("panen") && (
                                                <>
                                                    {canAccess('/operational/taksasi') && (
                                                        <MenuItem
                                                            _hover={{ bg: "gray.100" }}
                                                            _focus={{ bg: "gray.100" }}
                                                            py={3}
                                                            pl={8}
                                                            onClick={() => {
                                                                navigate('/operational/taksasi');
                                                                setOpenMenu(null);
                                                                setIsMobileMenuOpen(false);
                                                            }}
                                                        >
                                                            <Box flex="1">Taksasi</Box>
                                                        </MenuItem>
                                                    )}
                                                    {canAccess('/operational/rkh') && (
                                                        <>
                                                            <MenuItem
                                                                _hover={{ bg: "gray.100" }}
                                                                _focus={{ bg: "gray.100" }}
                                                                py={3}
                                                                pl={8}
                                                                onClick={() => {
                                                                    navigate('/operational/rkh');
                                                                    setOpenMenu(null);
                                                                    setIsMobileMenuOpen(false);
                                                                }}
                                                            >
                                                                <Box flex="1">RKH Detail</Box>
                                                            </MenuItem>
                                                        </>
                                                    )}
                                                    {canAccess('/operational/rkh/detail') && (
                                                        <>
                                                            <MenuItem
                                                                _hover={{ bg: "gray.100" }}
                                                                _focus={{ bg: "gray.100" }}
                                                                py={3}
                                                                pl={8}
                                                                onClick={() => {
                                                                    navigate('/operational/rkh/detail');
                                                                    setOpenMenu(null);
                                                                    setIsMobileMenuOpen(false);
                                                                }}
                                                            >
                                                                <Box flex="1">RKH Summary</Box>
                                                            </MenuItem>
                                                        </>
                                                    )}
                                                    {canAccess('/operational/harvest-monitoring') && (
                                                        <MenuItem
                                                            _hover={{ bg: "gray.100" }}
                                                            _focus={{ bg: "gray.100" }}
                                                            py={3}
                                                            pl={8}
                                                            onClick={() => {
                                                                navigate('/operational/harvest-monitoring');
                                                                setOpenMenu(null);
                                                                setIsMobileMenuOpen(false);
                                                            }}
                                                        >
                                                            <Box flex="1">Panen Monitoring</Box>
                                                        </MenuItem>
                                                    )}
                                                    {canAccess('/operational/harvest-monitoring-foreman') && (
                                                        <MenuItem
                                                            _hover={{ bg: "gray.100" }}
                                                            _focus={{ bg: "gray.100" }}
                                                            py={3}
                                                            pl={8}
                                                            onClick={() => {
                                                                navigate('/operational/harvest-monitoring-foreman');
                                                                setOpenMenu(null);
                                                                setIsMobileMenuOpen(false);
                                                            }}
                                                        >
                                                            <Box flex="1">Hasil Panen</Box>
                                                        </MenuItem>
                                                    )}
                                                    {canAccess('/operational/bkm-muat') && (
                                                        <MenuItem
                                                            _hover={{ bg: "gray.100" }}
                                                            _focus={{ bg: "gray.100" }}
                                                            py={3}
                                                            pl={8}
                                                            onClick={() => {
                                                                navigate('/operational/bkm-muat');
                                                                setOpenMenu(null);
                                                                setIsMobileMenuOpen(false);
                                                            }}
                                                        >
                                                            <Box flex="1">BKM Muat</Box>
                                                        </MenuItem>
                                                    )}
                                                    {canAccess('/operational/weighbridge') && (
                                                        <MenuItem
                                                            _hover={{ bg: "gray.100" }}
                                                            _focus={{ bg: "gray.100" }}
                                                            py={3}
                                                            pl={8}
                                                            onClick={() => {
                                                                navigate('/operational/weighbridge');
                                                                setOpenMenu(null);
                                                                setIsMobileMenuOpen(false);
                                                            }}
                                                        >
                                                            <Box flex="1">Laporan Timbang</Box>
                                                        </MenuItem>
                                                    )}
                                                    {canAccess('/operational/harvest-map') && (
                                                        <MenuItem
                                                            _hover={{ bg: "gray.100" }}
                                                            _focus={{ bg: "gray.100" }}
                                                            py={3}
                                                            pl={8}
                                                            onClick={() => {
                                                                navigate('/operational/harvest-map');
                                                                setOpenMenu(null);
                                                                setIsMobileMenuOpen(false);
                                                            }}
                                                        >
                                                            <Box flex="1">Peta Panen</Box>
                                                        </MenuItem>
                                                    )}
                                                    {canAccess('/operational/odoo-vs-sakti') && (
                                                        <MenuItem
                                                            _hover={{ bg: "gray.100" }}
                                                            _focus={{ bg: "gray.100" }}
                                                            py={3}
                                                            pl={8}
                                                            onClick={() => {
                                                                navigate('/operational/odoo-vs-sakti');
                                                                setOpenMenu(null);
                                                                setIsMobileMenuOpen(false);
                                                            }}
                                                        >
                                                            <Box flex="1">Odoo vs Sakti</Box>
                                                        </MenuItem>
                                                    )}

                                                    {/* Submenu Laporan Panen Sakti */}
                                                    <Box
                                                        display="flex"
                                                        justifyContent="space-between"
                                                        alignItems="center"
                                                        cursor="pointer"
                                                        px={3}
                                                        py={3}
                                                        pl={8}
                                                        _hover={{ bg: "gray.100" }}
                                                        onClick={(e) => handleSubmenuClick(e, "laporan-panen")}
                                                    >
                                                        <Box flex="1">Laporan</Box>
                                                        <ChevronRightIcon
                                                            transform={openSubmenus.has("laporan-panen") ? "rotate(90deg)" : "rotate(0deg)"}
                                                            transition="transform 0.2s"
                                                            fontSize="sm"
                                                        />
                                                    </Box>

                                                    {openSubmenus.has("laporan-panen") && (
                                                        <MenuItem
                                                            _hover={{ bg: "gray.100" }}
                                                            _focus={{ bg: "gray.100" }}
                                                            py={3}
                                                            pl={12}
                                                            onClick={() => {
                                                                setIsFactoryReceiptReportOpen(true);
                                                                setOpenMenu(null);
                                                                setIsMobileMenuOpen(false);
                                                            }}
                                                        >
                                                            <Box flex="1">Penerimaan Pabrik</Box>
                                                        </MenuItem>
                                                    )}
                                                </>
                                            )}
                                        </>
                                    )}

                                    {/* Peta Kebun - Moved from Produksi */}
                                    {canAccess('/estate-map') && (
                                        <MenuItem
                                            _hover={{ bg: "gray.100" }}
                                            _focus={{ bg: "gray.100" }}
                                            py={3}
                                            onClick={() => {
                                                navigate('/estate-map');
                                                setOpenMenu(null);
                                                setIsMobileMenuOpen(false);
                                            }}
                                        >
                                            <Box flex="1">Peta Produksi</Box>
                                        </MenuItem>
                                    )}
                                    {canAccess('/operational/upkeep-map') && (
                                        <MenuItem
                                            _hover={{ bg: "gray.100" }}
                                            _focus={{ bg: "gray.100" }}
                                            py={3}
                                            onClick={() => {
                                                navigate('/operational/upkeep-map');
                                                setOpenMenu(null);
                                                setIsMobileMenuOpen(false);
                                            }}
                                        >
                                            <Box flex="1">Peta Rawat</Box>
                                        </MenuItem>
                                    )}
                                    {canAccess('/operational/fuel_consumption_vs_lacak') && (
                                        <MenuItem
                                            _hover={{ bg: "gray.100" }}
                                            _focus={{ bg: "gray.100" }}
                                            py={3}
                                            onClick={() => {
                                                navigate('/operational/fuel_consumption_vs_lacak');
                                                setOpenMenu(null);
                                                setIsMobileMenuOpen(false);
                                            }}
                                        >
                                            <Box flex="1">Pengeluaran Solar Odoo vs Lacak</Box>
                                        </MenuItem>
                                    )}
                                </MenuList>
                            </Menu>
                        </Box>
                    )}

                    {/* Menu Finance */}
                    {canAccess('/finance/cash-flow') && (
                        <Box
                            position="relative"
                            width={{ base: "100%", md: "auto" }}
                            mb={{ base: 3, md: 0 }}
                            order={5}
                        >
                            <Menu
                                isOpen={openMenu === "finance"}
                                onClose={() => {
                                    setOpenMenu(null);
                                    setOpenSubmenus(new Set());
                                }}
                                strategy="fixed"
                                computePositionOnMount
                                gutter={0}
                                isLazy
                                autoSelect={false}
                            >
                                <MenuButton
                                    as={Button}
                                    rightIcon={<ChevronDownIcon />}
                                    variant="ghost"
                                    _hover={{ bg: { md: "rgba(255,255,255,0.1)" } }}
                                    _expanded={{ bg: { md: "rgba(255,255,255,0.1)" } }}
                                    color="white"
                                    fontWeight="bold"
                                    _active={{ bg: "transparent" }}
                                    width={{ base: "100%", md: "auto" }}
                                    justifyContent={{ base: "space-between", md: "center" }}
                                    textAlign="left"
                                    px={{ base: 3, md: 4 }}
                                    py={{ base: 3, md: 2 }}
                                    onClick={() => handleMenuClick("finance")}
                                >
                                    Finance
                                </MenuButton>
                                <MenuList
                                    bg="white"
                                    color="black"
                                    mt={0}
                                    borderTopRadius={0}
                                    minW={{ base: "calc(100vw - 32px)", md: "220px" }}
                                    position={{ base: "relative", md: "absolute" }}
                                    boxShadow={{ md: "lg" }}
                                    border={{ base: "none", md: "1px solid" }}
                                    borderColor={{ base: "transparent", md: "gray.200" }}
                                    py={0}
                                >
                                    {canAccess('/finance/cash-flow') && (
                                        <MenuItem
                                            _hover={{ bg: "gray.100" }}
                                            _focus={{ bg: "gray.100" }}
                                            py={3}
                                            onClick={() => {
                                                navigate('/finance/cash-flow');
                                                setOpenMenu(null);
                                                setIsMobileMenuOpen(false);
                                            }}
                                        >
                                            <Box flex="1">Cash Flow Dashboard</Box>
                                        </MenuItem>
                                    )}
                                </MenuList>
                            </Menu>
                        </Box>
                    )}
                </Flex>
            </Flex>

            {/* Logo - Diposisikan di tengah untuk mobile */}
            {/* <Box
            
                position="absolute"
                left={{ base: "50%", md: "auto" }}
                right={{ base: "auto", md: -4 }}
                transform={{ base: "translateX(6%)", md: "none" }}
                display="flex"
                alignItems="center"
                height="100%"
                maxH="100%"
                overflow="hidden"
                px={{ md: 4 }}
                // ml="auto"
            >
                <Image
                    src="/GBS-Web-Header.png"
                    alt="GBS Header"
                    height="100%"
                    width="auto"
                    objectFit="cover"
                    display={{ base: "none", sm: "block" }}
                />
                <Image
                    src="/GBS-Web-Header.png"
                    alt="GBS Logo"
                    height="100%"
                    width="auto"
                    objectFit="cover"
                    display={{ base: "block", sm: "none" }}
                />
            </Box> */}

            <Box
                position="absolute"
                top={0}
                right={0}
                display="flex"
                alignItems="center"
                height="100%"
                maxH="100%"
                overflow="hidden"
                px={{ md: 0 }}
            >
                <Image
                    src="/GBS-Web-Header.png"
                    alt="GBS Header"
                    height="100%"
                    width="auto"
                    objectFit="cover"
                    display={{ base: "none", sm: "block" }}
                />
                <Image
                    src="/GBS-Web-Header.png"
                    alt="GBS Logo"
                    height="100%"
                    width="auto"
                    objectFit="cover"
                    display={{ base: "block", sm: "none" }}
                />
            </Box>

            <ReportPopupProduksi
                isOpen={isReportProduksiOpen}
                onClose={() => setIsReportProduksiOpen(false)}
            />
            <RainfallReportPopup
                isOpen={isRainfallReportOpen}
                onClose={() => setIsRainfallReportOpen(false)}
            />
            <WaterLevelReportPopup
                isOpen={isWaterLevelReportOpen}
                onClose={() => setIsWaterLevelReportOpen(false)}
            />
            <FactoryReceiptReportPopup
                isOpen={isFactoryReceiptReportOpen}
                onClose={() => setIsFactoryReceiptReportOpen(false)}
            />
            <Reports
                isOpen={isReportOpen}
                onClose={() => setIsReportOpen(false)}
                initialReportType="inspeksi"
            />
        </Flex>
    );
};
export default Navbar;